import type { Mark, Node as PMNode, Schema } from "@tiptap/pm/model"
import { Fragment, Slice } from "@tiptap/pm/model"

/** A slot paragraph's content is `inline*` — text, an optional bold mark,
 *  and hardBreak, nothing else (mc_conformity_blocks._INLINE_TAG_RE mirrors
 *  exactly these two markers on the backend). Pasting arbitrary rich content
 *  (multiple paragraphs, headings, lists, links, other marks) would either
 *  get rejected outright or, worse, coerce ProseMirror into re-splitting the
 *  target paragraph to fit block-level nodes in — which is exactly the
 *  structural mutation `SlotParagraph`'s keymap guards exist to prevent.
 *
 *  This runs in `editorProps.transformPasted`, BEFORE the slice is fit into
 *  the document: every block boundary in the pasted content becomes a
 *  hardBreak, every mark except bold is dropped, and every non-text leaf
 *  (images, block atoms) is dropped. The result is always a single flat run
 *  of inline content with `openStart`/`openEnd` at 0, so pasting can only
 *  ever insert inline content at the cursor — never split or grow the
 *  document's node structure. */
export function flattenPastedSlice(slice: Slice, schema: Schema): Slice {
  const boldMarkType = schema.marks.bold
  const hardBreakType = schema.nodes.hardBreak

  const keepMarks = (marks: readonly Mark[]) =>
    boldMarkType ? marks.filter((mark) => mark.type === boldMarkType) : []

  const out: PMNode[] = []
  let needsSeparator = false

  const pushText = (node: PMNode) => {
    if (needsSeparator && hardBreakType) out.push(hardBreakType.create())
    out.push(node.mark(keepMarks(node.marks)))
    needsSeparator = false
  }

  const collect = (fragment: Fragment) => {
    fragment.forEach((node) => {
      if (node.isText) {
        pushText(node)
      } else if (hardBreakType && node.type === hardBreakType) {
        if (out.length > 0) out.push(hardBreakType.create())
        needsSeparator = false
      } else if (node.isBlock) {
        // A block boundary (paragraph, heading, list item, …): its own
        // content joins the same flat run, separated from whatever came
        // before it by a hard break — but only once something has actually
        // been emitted, so leading empty blocks don't produce a stray break.
        needsSeparator = out.length > 0
        collect(node.content)
      }
      // Any other inline atom (image, mention, …) is dropped: none exist in
      // this schema, and none should ever reach a slot paragraph.
    })
  }

  collect(slice.content)
  return new Slice(Fragment.from(out), 0, 0)
}
